import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Shield, Award, Users, Clock, CheckCircle } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            About Blue Knight Contracting & Construction
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Blue Knight Contracting is Ontario's trusted partner in construction, delivering quality projects 
            across Toronto, Ottawa, Mississauga, Hamilton, and beyond with over 20 years of experience.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-8 text-center">Our Story</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="text-lg mb-6">
                Founded in 2004, Blue Knight Contracting and Construction has grown from a small local contractor 
                to one of Ontario's most trusted names in construction. Our commitment to quality craftsmanship, 
                reliable service, and customer satisfaction has earned us a reputation for excellence across the province.
              </p>
              <p className="text-lg mb-6">
                What started as a vision to provide honest, professional construction services has evolved into a 
                comprehensive contracting company serving residential, commercial, and industrial clients throughout Ontario. 
                Our team of skilled professionals brings decades of combined experience to every project.
              </p>
              <p className="text-lg">
                Today, Blue Knight Contracting continues to uphold the values that built our reputation: integrity, 
                craftsmanship, and an unwavering commitment to our clients' success. We're proud to call Ontario home 
                and honored to contribute to the communities we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Our Values</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              These core values guide every decision we make and every project we undertake.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Integrity</h3>
                <p className="text-muted-foreground">
                  Honest communication, transparent pricing, and ethical business practices in everything we do.
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Quality</h3>
                <p className="text-muted-foreground">
                  Uncompromising commitment to craftsmanship and attention to detail in every project.
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Partnership</h3>
                <p className="text-muted-foreground">
                  Building lasting relationships with clients, suppliers, and communities across Ontario.
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Reliability</h3>
                <p className="text-muted-foreground">
                  On-time delivery, consistent communication, and dependable service you can count on.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Why Choose Blue Knight Contracting</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              With hundreds of successful projects across Ontario, we bring experience, expertise, and dedication to every job.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Licensed & Insured Across Ontario</h3>
                    <p className="text-muted-foreground">
                      Fully licensed, bonded, and insured with comprehensive coverage for all our services and your peace of mind.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Experienced Team of Professionals</h3>
                    <p className="text-muted-foreground">
                      Our skilled tradespeople and project managers bring years of experience and ongoing training to every project.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Comprehensive Service Offering</h3>
                    <p className="text-muted-foreground">
                      From initial design to final inspection, we handle every aspect of your construction project under one roof.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Local Ontario Expertise</h3>
                    <p className="text-muted-foreground">
                      Deep understanding of Ontario building codes, permit processes, and local market conditions.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Quality Guarantee</h3>
                    <p className="text-muted-foreground">
                      We stand behind our work with comprehensive warranties and ongoing support for all completed projects.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-muted/30 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6">By the Numbers</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">20+</div>
                  <div className="text-sm text-muted-foreground">Years in Business</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">500+</div>
                  <div className="text-sm text-muted-foreground">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">50+</div>
                  <div className="text-sm text-muted-foreground">Skilled Professionals</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">24/7</div>
                  <div className="text-sm text-muted-foreground">Emergency Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Experience the Blue Knight Difference
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
            Join hundreds of satisfied clients across Ontario who have trusted Blue Knight Contracting 
            with their most important construction projects.
          </p>
          <Button variant="hero" size="lg" className="text-lg px-8 py-4 bg-primary-foreground text-white hover:bg-primary-foreground/90">
            Start Your Project Today
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;